import type { ReactNode } from 'react';
import './report-grid.css';

/**
 * The one table the four reports and the chess matrix use. A column carries
 * both the rendered cell and the plain value that goes into an export, so the
 * file and the screen are built from the same definition.
 */

export interface ReportGridColumn<Row> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  numeric?: boolean;
  render(row: Row): ReactNode;
  exportValue?(row: Row): string;
}

export interface ReportGridProps<Row> {
  columns: ReadonlyArray<ReportGridColumn<Row>>;
  rows: readonly Row[];
  getRowKey(row: Row): string;
  totals?: ReadonlyArray<ReactNode>;
  emptyText?: string;
  testId?: string;
  stickyFirstColumn?: boolean;
}

export function ReportGrid<Row>({
  columns,
  rows,
  getRowKey,
  totals,
  emptyText,
  testId,
  stickyFirstColumn = false,
}: ReportGridProps<Row>): JSX.Element {
  const alignOf = (column: ReportGridColumn<Row>): string =>
    column.align === undefined ? '' : ` report-grid__cell--${column.align}`;

  return (
    <div className="report-grid__wrap">
      <table className="report-grid" data-testid={testId}>
        <thead className="report-grid__sticky">
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.key}
                scope="col"
                className={`${index === 0 && stickyFirstColumn ? 'report-grid__first ' : ''}report-grid__header${alignOf(column)}`}
                style={column.width === undefined ? undefined : { width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="report-grid__empty" colSpan={columns.length}>
                {emptyText ?? ''}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr key={getRowKey(row)} className={rowIndex % 2 === 1 ? 'report-grid__row--alt' : undefined}>
                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={`${index === 0 && stickyFirstColumn ? 'report-grid__first ' : ''}report-grid__cell${alignOf(column)}${column.numeric === true ? ' report-grid__cell--num' : ''}`}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {totals === undefined ? null : (
          <tfoot className="report-grid__totals">
            <tr>
              {columns.map((column, index) => (
                <td
                  key={column.key}
                  className={`${index === 0 && stickyFirstColumn ? 'report-grid__first ' : ''}report-grid__cell${alignOf(column)}${column.numeric === true ? ' report-grid__cell--num' : ''}`}
                >
                  {totals[index] ?? null}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
